/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { FlagRecord } from "@packages/sdk";
import * as RadioGroup from "@radix-ui/react-radio-group";

import type { ThemeType } from "../../../../../global/emotion";
import { numericPixels, radius, spacing } from "../../../../../global/emotion";
import { Text } from "../../../_base/_core/Text/Text.emotion";

const radioGroupItemStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: "inherit",
  border: `${numericPixels[2]} ${theme.semanticColors.onOverlayLow} solid`,
  ":is([data-state='checked'])": {
    backgroundColor: theme.semanticColors.accentLowest,
    border: `${numericPixels[2]} ${theme.semanticColors.accentLower} solid`,
  },
  borderRadius: radius.ms,
  cursor: "pointer",
  display: "inline-flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  padding: spacing.m,
  textAlign: "left",
  width: spacing.full,
});

const labelStyles: Record<string, Interpolation<ThemeType>> = {
  unchecked: (theme) => ({
    color: theme.semanticColors.primary,
  }),
  checked: (theme) => ({ color: theme.semanticColors.accentMedHigh }),
};

const sublabelStyles: Record<string, Interpolation<ThemeType>> = {
  unchecked: (theme) => ({
    color: theme.semanticColors.onOverlayMedium,
  }),
  checked: (theme) => ({ color: theme.semanticColors.accentHigher }),
};

export type FlagRecordReasonRadioInputItemProps = {
  label: string;
  sublabel: string;
  value: FlagRecord["reason"];
  checked: boolean;
};

export const FlagRecordReasonRadioInputItem = ({
  label,
  sublabel,
  value,
  checked,
}: FlagRecordReasonRadioInputItemProps) => {
  const analytics = useAnalytics();

  return (
    <RadioGroup.Item
      css={radioGroupItemStyle}
      value={value}
      onClick={() => {
        analytics.track({
          event: HallowAnalyticsEvent.TappedReportReason,
          properties: {
            reason: value,
            screen_name: "report_reason",
            type: "campaign",
          },
        });
      }}
    >
      <Text
        type="title"
        size="s"
        css={checked ? labelStyles.checked : labelStyles.unchecked}
      >
        {label}
      </Text>

      <Text
        type="detail"
        size="xl"
        css={checked ? sublabelStyles.checked : sublabelStyles.unchecked}
      >
        {sublabel}
      </Text>
    </RadioGroup.Item>
  );
};
